import { defineStore } from "pinia";
import { z } from "zod";
import { CustomerAddressType } from "~/types/ecom/address.type";
import type { User } from "~/types/ecom/user.type";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: <User|null>null,
    guest: <User | null>null,
    jwt_token_data: <string | null>null,
    error_message: <string | null>null,
    guest_error_message: <string | null>null,
    success_message: <string | null>null,
    is_guest_checkout: false,
    is_sign_in_button_loading: false,
    is_guest_sign_in_button_disabled: true,
    is_guest_sign_in_button_loading: false,
    is_forgot_password_button_loading: false,
    is_reset_password_button_loading: false,
    item: {
      username: "",
      password: "",
    },
    sign_in_form_error: {
      username: "",
      password: "",
    },
    forgot_password_item: {
      email: "",
    },
    forgot_password_error:{
      email:"",
    },
    reset_password_form_error:{
      token:"",
      email: "",
      password: "",
      password_confirmation: "",
    },
    guest_item: {
      title: "",
      email: "",
      first_name: "",
      last_name: "",
      mobile: "",
      terms_accepted: false,
      address: {
        type: CustomerAddressType.PRIMARY,
        town: "",
        postcode: "",
        line_1: "",
        line_2: "",
        line_3: "",
        county: "",
        country_id: useRuntimeConfig().public.appRegion === 'BE'? 9: 8 ,
      },
    },
    guest_form_error: {
      title:"",
      email: "",
      first_name: "",
      last_name: "",
      mobile: "",
      address: {
        type: CustomerAddressType.PRIMARY,
        town: "",
        postcode: "",
        line_1: "",
        line_2: "",
        line_3: "",
        county: "",
        country_id: useRuntimeConfig().public.appRegion === 'BE'? 9: 8 ,
      },
    },
    reset_password_item: {
      token: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
    active_form: "",
    sign_up_form: {
      title: "",
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      password: "",
      company: "",
      address: {
        type: CustomerAddressType.PRIMARY,
        town: "",
        postcode: "",
        line_1: "",
        line_2: "",
        line_3: "",
        country: "",
        country_id: useRuntimeConfig().public.appRegion === 'BE'? 9: 8 ,
      },
      contact_preferences: {
        email: false,
        mobile: false,
        sms: false,
        post: false,
      },
      terms_accepted: false,
    },
    is_sign_up_button_loading: false,
    title_options: [
      { name: "Meneer", code: "Mr" },
      { name: "Mevrouw", code: "Mrs" },
    ],
    sign_up_form_error_message: "",
    sign_up_form_error: {
      title: "",
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      password: "",
      company: "",
      address: {
        type: CustomerAddressType.PRIMARY,
        town: "",
        postcode: "",
        line_1: "",
        line_2: "",
        line_3: "",
        country: "",
        country_id: useRuntimeConfig().public.appRegion === 'BE'? 9: 8 ,
      },
      contact_preferences: {
        email: false,
        mobile: false,
        sms: false,
        post: false,
      },
      terms_accepted: ""
    },
    sign_up_address_incomplete_error: "",
    disabled_toggler_items: ["sms", "email"],
    show_customer_info: false,
    rememberMe: false,
  }),
  persist: [
    {
      pick: [
        "guest.id",
        "guest.token",
        "guest_item.title",
        "guest_item.email",
        "guest_item.first_name",
        "guest_item.last_name",
        "guest_item.mobile",
        "is_guest_checkout",
      ],
    },
    {
      pick: ["guest"],
      storage: import.meta.client ? localStorage : undefined,
    },

  ],
  getters: {
    is_authenticated: (state): boolean => !(state.user == null),
    is_guest: (state): boolean => !(state.guest == null),
  },
  actions: {
    async setCustomerTokenInCookie(customer: any) {
      const { setCustomerTokenInCookie } = await import('~/stores-datalayer/auth.datalayer');
      return await setCustomerTokenInCookie( customer);
    },

    async setAuthUserByToken(access_token: string,nuxtApp = null,runtimeConfig = null) {
      const { setAuthUserByToken } = await import('~/stores-datalayer/auth.datalayer');
      return await setAuthUserByToken(this, access_token,nuxtApp,runtimeConfig);
    },

    async setUserDefaultBranchOnSignIn(defaultBranchId: string) {
      const { setUserDefaultBranchOnSignIn } = await import('~/stores-datalayer/auth.datalayer');
      return await setUserDefaultBranchOnSignIn(defaultBranchId);
    },

    async setUserDefaultBranchOnRefresh(defaultBranchId: string) {
      const { setUserDefaultBranchOnRefresh } = await import('~/stores-datalayer/auth.datalayer');
      return await setUserDefaultBranchOnRefresh(defaultBranchId);
    },

    async postSignIn() {
      const { postSignIn } = await import('~/stores-datalayer/auth.datalayer');
      return await postSignIn();
    },

    async checkUsername() {
      const { checkUsername } = await import('~/stores-datalayer/auth.datalayer');
      return await checkUsername();
    },

    async resetSignIn() {
      const { resetSignIn } = await import('~/stores-datalayer/auth.datalayer');
        resetSignIn();
    },

    async sendForgotPasswordMail() {
      const { sendForgotPasswordMail } = await import('~/stores-datalayer/auth.datalayer');
      return await sendForgotPasswordMail();
    },

    async postResetPassword() {
      const { postResetPassword } = await import('~/stores-datalayer/auth.datalayer');
      return await postResetPassword();
    },

    async signOut() {
      const { signOut } = await import('~/stores-datalayer/auth.datalayer');
      return await signOut();
    },

    handlePasswordValidationOnSignUp(passwordErrorKey: string) {  
      if(!this.sign_up_form.password) {
        this.sign_up_form_error.password = "requiredPassword";
        return;
      }
      this.sign_up_form_error.password = passwordErrorKey;
    },

    async signUp(signUpSchema: z.ZodSchema, appRegion: string) {
      const { signUp } = await import('~/stores-datalayer/auth.datalayer');
      return await signUp(this, signUpSchema, appRegion);
    },

    async postSignUp() {
      const { postSignUp } = await import('~/stores-datalayer/auth.datalayer');
      return await postSignUp();
    },

    async postGuestSignUp(guestSchema: z.ZodSchema, appRegion: string) {
      const { postGuestSignUp } = await import('~/stores-datalayer/auth.datalayer');
      return await postGuestSignUp(guestSchema, appRegion);
    },

    async validatePassword(password: string) {
      const { validatePassword } = await import('~/stores-datalayer/auth.datalayer');
      return validatePassword(password);
    },

    async validateEmail(email: string) {
      const { validateEmail } = await import('~/stores-datalayer/auth.datalayer');
      return validateEmail(email);
    },

    async getEmptySignInFields() {
      const { getEmptySignInFields } = await import('~/stores-datalayer/auth.datalayer');
      return getEmptySignInFields();
    },

    async getStockNotifications() {
      const { getStockNotifications } = await import('~/stores-datalayer/auth.datalayer');
      return await getStockNotifications();
    },

    async onStockUnsubscribe(product_code: string) {
      const { onStockUnsubscribe } = await import('~/stores-datalayer/auth.datalayer');
      return await onStockUnsubscribe(product_code);
    },

    async fetchOneTimeQrCode(customerId: string) {
      const { fetchOneTimeQrCode } = await import('~/stores-datalayer/auth.datalayer');
      return await fetchOneTimeQrCode(customerId);
    },

    async fetchPreferredLanguage(customerId: string) {
      const { fetchPreferredLanguage } = await import('~/stores-datalayer/auth.datalayer');
      return await fetchPreferredLanguage(customerId);
    },

    async updatePreferredLanguage(customerId: string, localeCode: string) {
      const { updatePreferredLanguage } = await import('~/stores-datalayer/auth.datalayer');
      return await updatePreferredLanguage(customerId,localeCode);
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
